import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sword, Shield, Gem, Wand2, Sparkles } from "lucide-react";
import ItemChips from "./ItemChips";

const SLOT_META = {
  weapon:  { label: "Weapon",  Icon: Sword },
  armor:   { label: "Armor",   Icon: Shield },
  trinket: { label: "Trinket", Icon: Gem },
};

export default function EquippedPanel({ ch, setCh }) {
  const [busy, setBusy] = useState(false);
  const equipped = ch.equipped || { weapon: null, armor: null, trinket: null };
  const setBonus = ch.set_bonus;

  const unequip = async (slot) => {
    setBusy(true);
    try {
      setCh(await api.unequip(slot));
      toast("Unequipped");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const autoEquip = async () => {
    setBusy(true);
    try {
      const res = await api.autoEquip();
      setCh(res.character);
      toast.success(res.changes > 0 ? `Auto-equipped ${res.changes} slot${res.changes > 1 ? "s" : ""}` : "Already optimal");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel p-3" data-testid="equipped-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          Equipped
        </div>
        <button
          data-testid="auto-equip"
          onClick={autoEquip}
          disabled={busy}
          className="px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-30"
        >
          <Wand2 className="w-3 h-3" strokeWidth={1.5} /> Auto Best
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(SLOT_META).map(([slot, meta]) => {
          const item = equipped[slot];
          const Icon = meta.Icon;
          const classMatch = item && item.preferred_class === ch.class;
          return (
            <div
              key={slot}
              data-testid={`equipped-${slot}`}
              className={`panel-inner p-2 min-h-[70px] flex flex-col items-start ${
                item ? `border-l-4 rarity-${item.rarity}` : ""
              }`}
            >
              <div className="flex items-center gap-1 mb-1">
                <Icon className="w-3 h-3 text-[#8c8c8c]" strokeWidth={1.5} />
                <span className="font-heading text-[9px] uppercase tracking-widest text-[#666]">
                  {meta.label}
                </span>
              </div>
              {item ? (
                <>
                  <div className={`font-heading text-[10px] leading-tight truncate w-full rarity-${item.rarity}`}>
                    {item.name}
                  </div>
                  <div className="font-mono text-[9px] text-[#8c8c8c] mt-0.5">
                    +{item.applied_bonus || item.stat_bonus} {item.stat}
                    {classMatch && <span className="text-[#B89C3A]"> ★</span>}
                  </div>
                  {/* §13/§14: chip row shows elemental damage / resistance /
                      gm_effects for this equipped slot. */}
                  <ItemChips item={item} compact />
                  <button
                    data-testid={`unequip-${slot}`}
                    onClick={() => unequip(slot)}
                    disabled={busy}
                    className="mt-auto w-full py-0.5 border border-[#333] hover:border-[#8A0303] text-[9px] font-heading tracking-widest uppercase text-[#8c8c8c] hover:text-[#E33333]"
                  >
                    Unequip
                  </button>
                </>
              ) : (
                <div className="italic text-[10px] text-[#555] my-auto">Empty</div>
              )}
            </div>
          );
        })}
      </div>
      {setBonus ? (
        <div
          data-testid="set-bonus"
          className="mt-2 border border-[#B89C3A] bg-[#1a1610] p-2 flex items-center gap-2"
        >
          <Sparkles className="w-3 h-3 text-[#E5C34A]" strokeWidth={1.5} />
          <span className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]">
            {setBonus.class} Set Bonus: +{setBonus.amount} {setBonus.stat}
          </span>
        </div>
      ) : (
        <p className="text-[9px] italic text-[#666] mt-2">
          Items marked ★ match your class (+50% bonus). Wear a full 3-item set of one class to unlock a set bonus.
        </p>
      )}
    </div>
  );
}
